/* AppraisalComponents/FlashingEdgingBar.jsx
 * Phase C · C4d Flashing extraction (2026-02c).
 *
 * Edging bar for the Flashing section. Reads template-level
 * `edges.{start,end}` from each Flashing line's linked template and
 * aggregates total meters per edge type (Flashguard / AliEdge). Each
 * end fitted with the edge contributes one strip running the full
 * flashing length (qty × length). Operator can override the auto-
 * computed total m per type via the input fields.
 *
 * Extracted verbatim from AppraisalComponentsTab.js:5205-5299.
 */
import React from 'react';

function EdgingCard({ label, autoM, override, setOverride, testidPrefix, onCommit }) {
  return (
    <div className="rounded border border-teal-100 bg-white p-2 flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold text-slate-700">{label}</div>
        <div
          className="text-[10px] text-slate-500 font-mono-num"
          data-testid={`${testidPrefix}-auto-m`}
        >
          auto · {autoM.toFixed(2)} m
        </div>
      </div>
      <label className="flex items-center gap-1">
        <span className="text-[10px] text-slate-500 uppercase tracking-wide">Override m</span>
        <input
          type="number" min={0} step={0.1}
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          onBlur={onCommit}
          data-testid={`${testidPrefix}-override`}
          placeholder={autoM > 0 ? autoM.toFixed(1) : '—'}
          className="w-20 text-xs border border-slate-300 rounded px-2 py-1 font-mono-num bg-white"
        />
      </label>
    </div>
  );
}


export function FlashingEdgingBar({ lines, rates, templates, onCommit }) {
  const lineList = lines || [];
  const tpls = templates || [];

  // Aggregate meters per edge type from linked templates.
  const auto = React.useMemo(() => {
    const totals = { Flashguard: 0, AliEdge: 0 };
    const tplById = {};
    (tpls || []).forEach(t => { if (t && t._id) tplById[String(t._id)] = t; });
    lineList.forEach(l => {
      if (/^\[AUTO:/i.test(l.notes || '')) return;
      const tid = String(l.flashing_template_id || l.linked_flashing_template_id || '');
      if (!tid || !tplById[tid]) return;
      const edges = tplById[tid].edges || {};
      const runM = (Number(l.quantity) || 0) * (Number(l.length) || 0);
      if (runM <= 0) return;
      [edges.start, edges.end].forEach(et => {
        if (et === 'Flashguard') totals.Flashguard += runM;
        else if (et === 'AliEdge') totals.AliEdge += runM;
      });
    });
    return {
      Flashguard: Math.round(totals.Flashguard * 100) / 100,
      AliEdge: Math.round(totals.AliEdge * 100) / 100,
    };
  }, [lineList, tpls]);

  // Detect operator overrides stored on the current auto-lines.
  const existingFg  = lineList.find(l => /^\[AUTO:edging_flashguard\]/i.test(l.notes || ''));
  const existingAli = lineList.find(l => /^\[AUTO:edging_ali\]/i.test(l.notes || ''));
  const [fgOverride, setFgOverride]   = React.useState(existingFg  ? String(existingFg.quantity  || '') : '');
  const [aliOverride, setAliOverride] = React.useState(existingAli ? String(existingAli.quantity || '') : '');
  React.useEffect(() => {
    setFgOverride(existingFg  ? String(existingFg.quantity  || '') : '');
    setAliOverride(existingAli ? String(existingAli.quantity || '') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineList.length]);

  const hasAnyFlashing = lineList.some(l => !/^\[AUTO:/i.test(l.notes || ''));
  if (!hasAnyFlashing) return null;
  if (auto.Flashguard <= 0 && auto.AliEdge <= 0 && !existingFg && !existingAli) return null;

  const apply = () => onCommit({
    flashguard_m: fgOverride === '' ? (auto.Flashguard > 0 ? auto.Flashguard : null) : Number(fgOverride),
    ali_m:        aliOverride === '' ? (auto.AliEdge   > 0 ? auto.AliEdge   : null) : Number(aliOverride),
  });
  const clear = () => {
    setFgOverride('0');
    setAliOverride('0');
    onCommit({ flashguard_m: 0, ali_m: 0 });
  };

  return (
    <div
      data-testid="edging-bar"
      className="rounded-md border border-teal-200 bg-teal-50/40 px-3 py-2 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-700">
          🪞 Edging (Flashing) — auto-derived from linked templates
        </div>
        <button
          type="button"
          onClick={clear}
          data-testid="edging-clear-btn"
          className="text-[10px] px-2 py-0.5 rounded bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
        >Clear</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <EdgingCard label="Flashguard" autoM={auto.Flashguard} override={fgOverride}  setOverride={setFgOverride}  testidPrefix="edging-flashguard" onCommit={apply} />
        <EdgingCard label="Ali Edge"   autoM={auto.AliEdge}   override={aliOverride} setOverride={setAliOverride} testidPrefix="edging-ali"         onCommit={apply} />
      </div>
      <div className="text-[10px] text-slate-500 italic">
        Source: each linked Flashing template&apos;s edges.&#123;start,end&#125; field. Set the edge per template via the Trace Editor &rarr; &ldquo;Edging fitted&rdquo; picker.
      </div>
    </div>
  );
}
